/**
 * conso-search-suggest.js
 * Corrige erro de digitação na busca preditiva quando o resultado vem vazio.
 *
 * Ex.: "blaser" -> "blazer", "xtretch" -> "tretch" (casa X-Tretch).
 *
 * Só age quando a busca volta zero — nunca interfere numa busca que já
 * funciona. O vocabulário sai do próprio catálogo (section conso-search-vocab),
 * então não desatualiza quando entram produtos novos.
 *
 * Aditivo: não altera o predictive-search.js. Observa o container de resultados,
 * e quando detecta o estado vazio refaz a busca por conta própria com o termo
 * corrigido, montando a mesma URL que o tema monta.
 *
 * Antes de mostrar a correção, confere que ela REALMENTE retorna produtos. Se
 * não retornar, deixa o "sem resultado" original — melhor não achar nada do que
 * mandar a pessoa para um resultado inventado.
 */
(function () {
  'use strict';

  var VOCAB_SECTION = 'conso-search-vocab';
  // v2: o payload virou { vocab, aliases }. A versão no nome invalida cache
  // antigo em sessão que ainda esteja aberta.
  var VOCAB_CACHE_KEY = 'conso_search_vocab_v2';
  var RESULTS = '[data-search-results]';
  var NO_RESULTS = '.predictive-search-results__no-results';
  var INNER = '.predictive-search-results__inner';
  // Container que o tema substitui inteiro (ref="predictiveSearchResults"). É
  // nele que o markup da section entra — trocar o INNER aninharia uma section
  // dentro da outra.
  var CONTENT = '.predictive-search-form__content';

  // Espelha o predictive-search.js. Se a exclusão de outlet mudar lá, muda aqui.
  var OUTLET_EXCLUSION = 'NOT tag:outlet ';

  var vocabPromise = null;
  var skipTerms = {}; // termos onde o usuário pediu o resultado original
  var pending = null; // termo em processamento, evita fetch duplicado por mutação

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // remove acento: "suéter" -> "sueter"
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function tokenize(value) {
    return normalize(value)
      .split(' ')
      .filter(function (w) {
        return w.length >= 3;
      });
  }

  /** Distância de Levenshtein, com corte rápido por diferença de tamanho. */
  function distance(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 99;
    var prev = [];
    for (var j = 0; j <= b.length; j++) prev[j] = j;
    for (var i = 1; i <= a.length; i++) {
      var cur = [i];
      for (var k = 1; k <= b.length; k++) {
        cur[k] = Math.min(prev[k] + 1, cur[k - 1] + 1, prev[k - 1] + (a[i - 1] === b[k - 1] ? 0 : 1));
      }
      prev = cur;
    }
    return prev[b.length];
  }

  function loadVocab() {
    if (vocabPromise) return vocabPromise;

    var cached = null;
    try {
      cached = sessionStorage.getItem(VOCAB_CACHE_KEY);
    } catch (e) {
      /* modo privado bloqueia storage — segue sem cache */
    }
    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.vocab) {
          vocabPromise = Promise.resolve(parsed);
          return vocabPromise;
        }
      } catch (e) {
        /* cache corrompido — refaz */
      }
    }

    var url = new URL(window.location.origin + '/');
    url.searchParams.set('section_id', VOCAB_SECTION);

    vocabPromise = fetch(url.toString())
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-conso-search-vocab]');
        var payload = node ? JSON.parse(node.textContent) : {};

        var words = {};
        (payload.titles || []).forEach(function (title) {
          tokenize(title).forEach(function (w) {
            words[w] = true;
          });
        });

        // Achata para um mapa `termo normalizado -> alvo`.
        var aliases = {};
        (payload.aliases || []).forEach(function (rule) {
          if (!rule || !rule.para) return;
          var target = normalize(rule.para);
          (rule.de || []).forEach(function (from) {
            var key = normalize(from);
            if (key) aliases[key] = target;
          });
        });

        var data = { vocab: Object.keys(words), aliases: aliases };

        try {
          sessionStorage.setItem(VOCAB_CACHE_KEY, JSON.stringify(data));
        } catch (e) {
          /* sem storage, tudo bem */
        }
        return data;
      })
      .catch(function () {
        return { vocab: [], aliases: {} };
      });

    return vocabPromise;
  }

  /**
   * Corrige cada palavra do termo. Palavras curtas ficam de fora porque a
   * distância de edição vira ruído: com 3 letras quase tudo fica a 1 de tudo.
   */
  function correct(term, vocab, aliases) {
    var whole = normalize(term);
    if (!whole) return '';

    // Apelido tem precedência sobre a correção automática, e é conferido
    // primeiro na frase inteira: quem digita "ultra stretch" quer a Flex Ultra,
    // e o fuzzy sozinho mandaria "stretch" para "tretch" (a X-Tretch).
    if (aliases[whole]) return aliases[whole];

    if (!vocab.length) return '';

    var words = tokenize(term);
    if (!words.length) return '';

    var changed = false;
    var out = words.map(function (word) {
      if (aliases[word]) {
        changed = true;
        return aliases[word];
      }
      if (word.length < 4) return word;
      if (vocab.indexOf(word) !== -1) return word;

      var maxDist = word.length >= 6 ? 2 : 1;
      var best = null;
      var bestDist = 99;

      for (var i = 0; i < vocab.length; i++) {
        var d = distance(word, vocab[i]);
        if (d < bestDist) {
          bestDist = d;
          best = vocab[i];
        }
      }

      if (best && bestDist <= maxDist) {
        changed = true;
        return best;
      }
      return word;
    });

    return changed ? out.join(' ') : '';
  }

  /** Monta a mesma URL que o predictive-search.js monta. */
  function searchUrl(term) {
    var url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', OUTLET_EXCLUSION + term);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'predictive-search');
    return url.toString();
  }

  function notice(original, corrected, input) {
    var wrap = document.createElement('div');
    wrap.className = 'conso-search-suggest';
    wrap.setAttribute('role', 'status');
    wrap.style.cssText =
      'padding:0 var(--padding-xl,1rem);margin-block-end:var(--margin-sm,.5rem);' +
      'font-size:var(--font-size--body-sm,.875rem);color:rgb(var(--color-foreground-rgb,0 0 0)/.7);';

    var msg = document.createElement('span');
    msg.textContent = 'Resultados para ';
    var strong = document.createElement('strong');
    strong.textContent = corrected;
    strong.style.color = 'var(--color-foreground,inherit)';
    msg.appendChild(strong);
    wrap.appendChild(msg);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Buscar por "' + original + '" mesmo assim';
    btn.style.cssText =
      'display:block;margin-block-start:2px;background:none;border:0;padding:0;' +
      'font:inherit;color:inherit;text-decoration:underline;cursor:pointer;';
    btn.addEventListener('click', function () {
      // Marca o termo e redispara a busca do tema: o observer abaixo vê a flag,
      // não corrige, e o estado real de "sem resultado" aparece.
      skipTerms[normalize(original)] = true;
      if (input) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    wrap.appendChild(btn);

    return wrap;
  }

  function handle(component, container) {
    if (!container.querySelector(NO_RESULTS)) return;
    if (container.querySelector('.conso-search-suggest')) return;

    var input = component.querySelector('.predictive-search-form .search-input');
    var term = input ? input.value.trim() : '';
    if (!term) return;

    var key = normalize(term);
    if (!key || skipTerms[key] || pending === key) return;
    pending = key;

    var done = function () {
      if (pending === key) pending = null;
    };

    loadVocab()
      .then(function (data) {
        var fixed = correct(term, data.vocab, data.aliases);
        if (!fixed || fixed === key) return;

        return fetch(searchUrl(fixed))
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            // O termo pode ter mudado enquanto o fetch corria.
            if (!input || input.value.trim() !== term) return;

            var doc = new DOMParser().parseFromString(html, 'text/html');
            // Só troca se a correção realmente achou algo. Se não achou, a
            // escolha do vocabulário foi ruim e o "sem resultado" original é
            // mais honesto do que um resultado inventado.
            if (doc.querySelector(NO_RESULTS) || !doc.querySelector('.predictive-search-results__card')) return;

            var content = component.querySelector(CONTENT);
            if (!content) return;

            content.innerHTML = html;

            var inner = content.querySelector(INNER);
            if (inner) inner.insertBefore(notice(term, fixed, input), inner.firstChild);
          });
      })
      .catch(function () {
        /* falha de rede: mantém o "sem resultado" original */
      })
      .then(done, done);
  }

  function watch(component) {
    if (component.dataset.consoSuggestWatched) return;
    component.dataset.consoSuggestWatched = '1';

    new MutationObserver(function () {
      var container = component.querySelector(RESULTS);
      if (container) handle(component, container);
    }).observe(component, { childList: true, subtree: true });
  }

  function init() {
    var list = document.querySelectorAll('predictive-search-component');
    for (var i = 0; i < list.length; i++) watch(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
